import {
  extractSdkErrorDetail,
  getResponseStatus,
  PROTECTED_BRANCH,
  readConflictDetail,
  STALE_SYSTEM_HEAD,
} from "@istari-frontend/shared";

import { PROTECTED_BRANCH_REASON } from "~/branching/model/protected-branch";

export type PublishingProblemKind =
  | "forbidden"
  | "not-found"
  | "validation"
  | "stale-head"
  | "package-conflict"
  | "protected-branch"
  | "publishing-disabled"
  | "download-too-large"
  | "unknown";

/**
 * A failed library or package request, classified by what the user can do about it. The message is
 * the server's explanation when it gave one, otherwise the request's own fallback. The original SDK
 * error stays on `cause`, so status checks such as the query retry policy still see the response.
 */
export class PublishingProblem extends Error {
  readonly kind: PublishingProblemKind;

  constructor(kind: PublishingProblemKind, message: string, cause?: unknown) {
    super(message, { cause });
    this.name = "PublishingProblem";
    this.kind = kind;
  }
}

/** Awaits a library or package request and rethrows any failure as a PublishingProblem. */
export async function rethrowAsPublishingProblem<T>(
  request: Promise<T>,
  fallback: string,
  options: { downloadSubject?: "package" | "system" | "file" } = {},
): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (error instanceof PublishingProblem) throw error;
    if (options.downloadSubject && getResponseStatus(error) === 413) {
      const suggestion = options.downloadSubject === "package" ? " Download individual files instead." : "";
      throw new PublishingProblem(
        "download-too-large",
        `This ${options.downloadSubject} cannot be downloaded because it is too large.${suggestion}`,
        error,
      );
    }
    throw await classifyPublishingError(error, fallback);
  }
}

/** Any failure as a PublishingProblem; one that did not come through the library data layer reads as `fallback`. */
export function publishingProblemOf(error: unknown, fallback: string): PublishingProblem {
  return error instanceof PublishingProblem ? error : new PublishingProblem("unknown", fallback, error);
}

const CONFLICT_KINDS: Record<string, PublishingProblemKind> = {
  [STALE_SYSTEM_HEAD]: "stale-head",
  PACKAGE_CONFLICT: "package-conflict",
};

export function isConflictKind(kind: PublishingProblemKind): boolean {
  return Object.values(CONFLICT_KINDS).includes(kind);
}

export function isProtectedBranchProblem(error: unknown): error is PublishingProblem & { kind: "protected-branch" } {
  return error instanceof PublishingProblem && error.kind === "protected-branch";
}

export async function classifyPublishingError(error: unknown, fallback: string): Promise<PublishingProblem> {
  const status = getResponseStatus(error);
  const conflict = status === 409 ? await readConflictDetail(error) : undefined;
  const kind = kindOf(status, conflict?.errorCode);
  if (kind === "protected-branch") return new PublishingProblem(kind, PROTECTED_BRANCH_REASON, error);
  const detail = await extractSdkErrorDetail(error, { joinValidationDetail: true });
  const message = (detail && sentenceOf(detail)) ?? conflict?.message ?? fallback;
  return new PublishingProblem(kind, message, error);
}

/** The registry sends a refusal as `Permission Denied: "<sentence>"`; the sentence is what the reader needs. */
function sentenceOf(detail: string): string {
  return /^[A-Z][A-Za-z ]*: "(.+)"$/s.exec(detail)?.[1] ?? detail;
}

function kindOf(status: number | undefined, conflictCode: string | undefined): PublishingProblemKind {
  if (status === 403) return "forbidden";
  if (status === 404) return "not-found";
  if (status === 400 || status === 422) return "validation";
  if (status === 503) return "publishing-disabled";
  if (status === 409 && conflictCode === PROTECTED_BRANCH) return "protected-branch";
  if (status === 409) return (conflictCode && CONFLICT_KINDS[conflictCode]) || "package-conflict";
  return "unknown";
}
